// Azure Table Storage Service for Dashboard Configurations
// Table: dashboardConfig, PartitionKey: "dashboard", RowKey: "{userId}_{dashboardId}"

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "dashboardConfig",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const getBaseUrl = () =>
  `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}`;

const READ_HEADERS = { Accept: "application/json;odata=nometadata" };
const WRITE_HEADERS = {
  "Content-Type": "application/json",
  Accept: "application/json;odata=nometadata",
};

// Ensure the table exists (create if not)
const ensureTable = async () => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/Tables?${AZURE_CONFIG.sasToken}`;
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json;odata=nometadata" },
      body: JSON.stringify({ TableName: AZURE_CONFIG.tableName }),
    });
  } catch {
    // Table might already exist - ignore
  }
};

let tableEnsured = false;

/**
 * Get all dashboards for a user
 */
export const getDashboards = async (userId) => {
  if (!tableEnsured) {
    await ensureTable();
    tableEnsured = true;
  }

  const filter = `PartitionKey eq 'dashboard' and UserId eq '${userId}'`;
  let allResults = [];
  let nextPartitionKey = null;
  let nextRowKey = null;

  do {
    let url = `${getBaseUrl()}?${AZURE_CONFIG.sasToken}&$filter=${encodeURIComponent(filter)}`;

    if (nextPartitionKey && nextRowKey) {
      url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
      url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
    }

    const response = await fetch(url, { headers: READ_HEADERS });

    if (!response.ok) {
      throw new Error(`Fehler beim Laden der Dashboards: ${response.status}`);
    }

    const data = await response.json();
    allResults = allResults.concat(data.value || []);

    nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
    nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
  } while (nextPartitionKey && nextRowKey);

  return allResults.map(entityToDashboard);
};

/**
 * Save (create or update) a dashboard
 */
export const saveDashboard = async (dashboard) => {
  if (!tableEnsured) {
    await ensureTable();
    tableEnsured = true;
  }

  const rowKey = `${dashboard.userId}_${dashboard.id}`;
  const now = new Date().toISOString();

  const entity = {
    PartitionKey: "dashboard",
    RowKey: rowKey,
    DashboardId: dashboard.id,
    UserId: dashboard.userId,
    Name: dashboard.name,
    Widgets: JSON.stringify(dashboard.widgets || []),
    CreatedAt: dashboard.createdAt || now,
    UpdatedAt: now,
  };

  const url = `${getBaseUrl()}(PartitionKey='dashboard',RowKey='${encodeURIComponent(rowKey)}')?${AZURE_CONFIG.sasToken}`;

  const response = await fetch(url, {
    method: "PUT",
    headers: WRITE_HEADERS,
    body: JSON.stringify(entity),
  });

  if (!response.ok) {
    throw new Error(`Fehler beim Speichern des Dashboards: ${response.status}`);
  }

  return { ...dashboard, updatedAt: now, createdAt: entity.CreatedAt };
};

/**
 * Delete a dashboard
 */
export const deleteDashboard = async (userId, dashboardId) => {
  const rowKey = `${userId}_${dashboardId}`;
  const url = `${getBaseUrl()}(PartitionKey='dashboard',RowKey='${encodeURIComponent(rowKey)}')?${AZURE_CONFIG.sasToken}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: { "If-Match": "*" },
  });

  if (!response.ok && response.status !== 404) {
    throw new Error(`Fehler beim Löschen des Dashboards: ${response.status}`);
  }
};

// Convert Azure entity to dashboard object
const entityToDashboard = (entity) => ({
  id: entity.DashboardId,
  userId: entity.UserId,
  name: entity.Name,
  widgets: JSON.parse(entity.Widgets || "[]"),
  createdAt: entity.CreatedAt,
  updatedAt: entity.UpdatedAt,
});

// localStorage fallback helpers
const LS_KEY_PREFIX = "mf-dashboards-";

export const getDashboardsLocal = (userId) => {
  try {
    const raw = localStorage.getItem(`${LS_KEY_PREFIX}${userId}`);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const saveDashboardsLocal = (userId, dashboards) => {
  try {
    localStorage.setItem(`${LS_KEY_PREFIX}${userId}`, JSON.stringify(dashboards));
  } catch {
    // localStorage full or unavailable
  }
};
